<script setup lang="ts">
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import EventsPartner from './EventsPartner.vue';
import ContentNews from '../content/ContentNews.vue';
import ContentCompanies from '../content/ContentCompanies.vue';
import Banner from '~/shared/ui/banner/Banner.vue';
import {
  CLIENT_COMPANIES_FEED_KEY,
  CLIENT_COMPANIES_FEED_URL,
  CLIENT_NEWS_FEED_KEY,
  CLIENT_NEWS_FEED_URL,
} from '~/shared/api/client/publisher/publisher.const';
import type { IFeedDataClientDTO } from '~/shared/api/client/feed/feed.dto';
import { FEED_BANNER_BOTTOM_PARAMS } from '~/shared/lib/banners';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { useStickyHeight } from '~/shared/lib/useStickyHeight';

const isDesktop = computed(() =>
  useNuxtApp().$viewport.isGreaterOrEquals('tabletBig'),
);

const activeTab = ref(isDesktop.value ? 'news' : 'main');

watch(
  () => isDesktop.value,
  (newValue) => {
    if (newValue && activeTab.value === 'main') {
      activeTab.value = 'news';
    }

    if (!newValue && activeTab.value === 'news') {
      activeTab.value = 'main';
    }
  },
);

const mainAreaRef = ref<HTMLElement | null>(null);
const sidebarRef = ref<HTMLElement | null>(null);

useStickyHeight([mainAreaRef, sidebarRef]);

const { data: feedData, status: feedStatus } = useCustomFetch<
  IFeedDataClientDTO[]
>(CLIENT_NEWS_FEED_URL, {
  method: 'GET',
  key: `${CLIENT_NEWS_FEED_KEY}_events`,
});

const { data: feedCompanyData, status: feedCompanyStatus } = useCustomFetch<
  IFeedDataClientDTO[]
>(CLIENT_COMPANIES_FEED_URL, {
  method: 'GET',
  key: `${CLIENT_COMPANIES_FEED_KEY}_events`,
});
</script>

<template>
  <div :class="$style.layout">
    <div ref="mainAreaRef" :class="$style.mainArea">
      <TabsRoot v-model="activeTab" :class="$style.tabs">
        <TabsList
          v-if="!isDesktop"
          aria-label="Главные новости и Новости компаний"
          :class="$style.categorySelector"
        >
          <TabsTrigger
            as="div"
            :class="$style.categorySelector__item"
            value="main"
          >
            Главное
          </TabsTrigger>
          <TabsTrigger
            as="div"
            :class="$style.categorySelector__item"
            value="news"
          >
            Новости
          </TabsTrigger>
          <TabsTrigger
            as="div"
            :class="$style.categorySelector__item"
            value="companies"
          >
            Компании
          </TabsTrigger>
        </TabsList>
        <TabsContent v-if="!isDesktop" value="news" :class="$style.tabContent">
          <ContentNews :data="feedData?.slice(1)" :status="feedStatus" />
        </TabsContent>
        <TabsContent
          v-if="!isDesktop"
          value="companies"
          :class="$style.tabContent"
        >
          <ContentCompanies
            :data="feedCompanyData?.slice(1)"
            :status="feedCompanyStatus"
          />
        </TabsContent>
      </TabsRoot>
      <main v-show="isDesktop || activeTab === 'main'" :class="$style.main">
        <slot />
      </main>
    </div>
    <aside v-if="isDesktop" ref="sidebarRef" :class="$style.sidebar">
      <EventsPartner />
      <Banner
        :class="$style.banner"
        :config="[
          FEED_BANNER_BOTTOM_PARAMS,
          FEED_BANNER_BOTTOM_PARAMS,
          FEED_BANNER_BOTTOM_PARAMS,
        ]"
        :index="-1"
        align="left"
        :width="300"
      />
      <div :class="$style.feed">
        <TabsRoot v-model="activeTab" :class="$style.tabs">
          <TabsList
            aria-label="Новости и Компании"
            :class="$style.categorySelector"
          >
            <TabsTrigger
              as="div"
              :class="$style.categorySelector__item"
              value="news"
            >
              Новости
            </TabsTrigger>
            <TabsTrigger
              as="div"
              :class="$style.categorySelector__item"
              value="companies"
            >
              Компании
            </TabsTrigger>
          </TabsList>
          <TabsContent value="news" :class="$style.content">
            <ContentNews :data="feedData?.slice(1)" :status="feedStatus" />
          </TabsContent>
          <TabsContent value="companies" :class="$style.content">
            <ContentCompanies
              :data="feedCompanyData?.slice(1)"
              :status="feedCompanyStatus"
            />
          </TabsContent>
        </TabsRoot>
      </div>
    </aside>
  </div>
</template>

<style module lang="scss">
.layout {
  display: flex;
  min-height: calc(100vh - 64px);
  position: relative;
  z-index: 1;
}

.mainArea {
  position: sticky;
  top: min(64px, calc(100vh - var(--sticky-height, 100vh)));
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  border-right: 1px solid var(--color-gray-line);
}

.sidebar {
  position: sticky;
  top: min(64px, calc(100vh - var(--sticky-height, 100vh)));
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 300px;
}

.feed {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.banner {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  & > div {
    width: 300px;
  }
}

.tabs {
  display: flex;
  flex-flow: column nowrap;
}

.tabContent {
  width: 100%;
}

.categorySelector {
  display: flex;
  flex-flow: row nowrap;
  height: 40px;
  border-bottom: 1px solid var(--color-gray-line);
  flex-shrink: 0;

  &__item {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    @include text-body2_medium;
    color: var(--color-gray-text);
    background-color: var(--color-gray-bg);
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: var(--color-red-1);
    }

    &[data-state='active'] {
      color: var(--color-black-1);
      background-color: var(--color-white);

      &:hover {
        color: var(--color-red-1);
      }
    }
  }
}

.content {
  width: 300px;
  flex-shrink: 0;
}

@media (max-width: 1023px) {
  .layout {
    min-height: calc(100vh - 64px);
  }

  .mainArea {
    position: static;
    align-self: stretch;
  }

  .main {
    border-right: none;
  }

  .categorySelector {
    &__item {
      &:nth-child(2) {
        border-left: 1px solid var(--color-gray-line);
        border-right: 1px solid var(--color-gray-line);
      }
    }
  }
}
</style>
